import type { MailMentionItemDto } from "../../services/mailNoteService";

/**
 * SP8 — @-Erwähnungen-Ansicht (Mail-Topbar): reine Hilfslogik für die
 * Mention-Inbox (`GET /mail/notes/mentions/me`). Kein React, damit die Zähl-
 * und Gruppier-Logik unabhängig testbar bleibt (vgl. `mailFilter.ts`).
 *
 * Nur KMVS-Mentions (Entscheidung 24) — keine Outlook-`mentionsPreview`.
 */

/** Anzahl der ungelesenen Erwähnungen (= Badge-Zahl an der Topbar). */
export function unreadMentionCount(list: readonly MailMentionItemDto[]): number {
    return list.reduce((n, m) => (m.read ? n : n + 1), 0);
}

/** Zeitstempel einer Erwähnung als ms; ungültige/leere sortieren nach ganz unten. */
function tsMillis(m: MailMentionItemDto): number {
    const t = m.timestamp ? new Date(m.timestamp).getTime() : NaN;
    return Number.isNaN(t) ? -Infinity : t;
}

/** Erwähnungen chronologisch, neueste zuerst (stabil bei Gleichstand). */
export function sortMentionsNewestFirst(
    list: readonly MailMentionItemDto[],
): MailMentionItemDto[] {
    return list
        .map((m, i) => ({ m, i }))
        .sort((a, b) => {
            const d = tsMillis(b.m) - tsMillis(a.m);
            return d !== 0 ? d : a.i - b.i;
        })
        .map((x) => x.m);
}

const MONTHS = [
    "Januar", "Februar", "März", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Dezember",
];

/**
 * Datums-Gruppenlabel wie die Mail-Liste (`getDateGroup` in MailView):
 * Heute / Gestern / Letzte 7 Tage / „Monat JJJJ".
 */
export function mentionDateGroup(date: Date, reference: Date): string {
    const refDay = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
    const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((refDay.getTime() - day.getTime()) / 86400000);
    if (diffDays <= 0) return "Heute";
    if (diffDays === 1) return "Gestern";
    if (diffDays <= 7) return "Letzte 7 Tage";
    return `${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

export type MailMentionGroup = {
    label: string;
    items: MailMentionItemDto[];
};

/**
 * Erwähnungen (neueste zuerst) nach Datum gruppiert; die Gruppen erscheinen in
 * chronologisch absteigender Reihenfolge (Heute → … → ältere Monate). Einträge
 * ohne gültigen Zeitstempel landen in einer Sammelgruppe „Ohne Datum".
 */
export function groupMentionsByDate(
    list: readonly MailMentionItemDto[],
    reference: Date = new Date(),
): MailMentionGroup[] {
    const sorted = sortMentionsNewestFirst(list);
    const groups: MailMentionGroup[] = [];
    const byLabel = new Map<string, MailMentionGroup>();
    for (const m of sorted) {
        const t = m.timestamp ? new Date(m.timestamp) : null;
        const label = t && !Number.isNaN(t.getTime()) ? mentionDateGroup(t, reference) : "Ohne Datum";
        let g = byLabel.get(label);
        if (!g) {
            g = { label, items: [] };
            byLabel.set(label, g);
            groups.push(g);
        }
        g.items.push(m);
    }
    return groups;
}
